import cn from "classnames";

import { Tag, Tooltip, Typography } from "antd";
import { Children, ReactElement, cloneElement, isValidElement } from "react";
import type { CSSProperties, ReactNode } from "react";
import type { TagProps } from "antd";

import "./style.scss";

interface InfoGridProps {
  columns?: number;
  className?: string;
  labelColumnWidth?: number | string;
  children?: ReactNode;
}

interface InfoGridItemProps {
  label: ReactNode;
  content: ReactNode;
  className?: string;
  labelClassName?: string;
  contentClassName?: string;
  labelStyle?: CSSProperties;
  contentStyle?: CSSProperties;
  isHtml?: boolean;
  labelColumnWidth?: number | string;
  lines?: number;
  labelTooltip?: ReactNode;
  contentTooltip?: ReactNode;
  fullWidth?: boolean; // Item chiếm 1 hàng ngang toàn bộ
}

interface InfoGridTagProps extends Omit<InfoGridItemProps, "isHtml"> {
  tagProps?: TagProps;
}

const JS_PROTOCOL_PREFIX = "javascript:";

const sanitizeHtml = (html: string) => {
  if (!html) return "";

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Loại bỏ script/style
    doc.querySelectorAll("script,style").forEach((node) => node.remove());

    // Loại bỏ các thuộc tính on*, javascript: trong href/src
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT, null);
    let current: Node | null = walker.nextNode();

    while (current) {
      const el = current as Element;
      Array.from(el.attributes).forEach((attr) => {
        const name = attr.name || "";
        const value = attr.value || "";

        if (name.startsWith("on")) {
          el.removeAttribute(name);
        }

        if (
          (name === "href" || name === "src" || name === "xlink:href") &&
          value.trim().toLowerCase().startsWith(JS_PROTOCOL_PREFIX)
        ) {
          el.removeAttribute(name);
        }
      });

      current = walker.nextNode();
    }

    // Chuyển h1..h6, p sang div để tránh style ngoài ý muốn
    const headingTags = ["h1", "h2", "h3", "h4", "h5", "h6", "p"];
    headingTags.forEach((tag) => {
      doc.querySelectorAll(tag).forEach((el) => {
        const div = doc.createElement("div");
        div.innerHTML = el.innerHTML;
        div.setAttribute("style", "margin:0;padding:0;");
        el.replaceWith(div);
      });
    });

    return doc.body.innerHTML;
  } catch {
    // Fallback: strip toàn bộ tag HTML
    return html.replace(/<[^>]*>?/gm, "");
  }
};

const stripHtml = (value: string) => value.replace(/<[^>]*>?/gm, "");

const CLAMP_CLASS_MAP: Record<number, string> = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
  5: "line-clamp-5",
};

const getClampClass = (lines: number) => CLAMP_CLASS_MAP[lines];

const getClampStyle = (lines: number): CSSProperties => {
  if (lines <= 1) {
    return {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    };
  }

  return {
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  } as CSSProperties;
};

const shouldShowTooltip = (title?: ReactNode) => {
  if (title === undefined || title === null) return false;
  if (typeof title === "string") return title.trim().length > 0;
  return true;
};

const wrapWithTooltip = <T extends ReactElement>(node: T, title?: ReactNode) =>
  shouldShowTooltip(title) ? <Tooltip title={title}>{node}</Tooltip> : node;

const ensurePositiveColumns = (columns?: number): number => {
  if (!columns || columns <= 0) return 1;
  return Math.floor(columns);
};

const Root = ({ columns = 2, className, children, labelColumnWidth = 160 }: InfoGridProps) => {
  const resolvedColumns = ensurePositiveColumns(columns);

  const childArray = Children.toArray(children).filter(isValidElement) as ReactElement<InfoGridItemProps>[];

  const resolvedLabelWidth =
    typeof labelColumnWidth === "number" ? `${labelColumnWidth}px` : labelColumnWidth || "160px";

  return (
    <div
      className={cn("info-grid", className)}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${resolvedColumns}, minmax(0, 1fr))`,
      }}
    >
      {childArray.map((child, index) => {
        const isFullWidth = child.props.fullWidth;

        return (
          <div key={child.key ?? index} style={isFullWidth ? { gridColumn: `1 / -1` } : undefined}>
            {cloneElement(child, {
              labelColumnWidth: child.props.labelColumnWidth ?? resolvedLabelWidth,
            })}
          </div>
        );
      })}
    </div>
  );
};

const Item = ({
  label,
  content,
  className,
  labelClassName,
  contentClassName,
  labelStyle,
  contentStyle,
  isHtml,
  labelColumnWidth,
  lines = 1,
  labelTooltip,
  contentTooltip,
}: // fullWidth được xử lý bởi Root component
InfoGridItemProps) => {
  const resolvedLines = Math.max(1, lines);
  const clampClass = getClampClass(resolvedLines);
  const clampStyle = getClampStyle(resolvedLines);

  const defaultLabelTooltip = typeof label === "string" || typeof label === "number" ? label : undefined;

  const sanitizedHtml = isHtml ? sanitizeHtml(String(content ?? "")) : undefined;

  const defaultContentTooltip = isHtml
    ? stripHtml(sanitizedHtml ?? "")
    : typeof content === "string" || typeof content === "number"
    ? content
    : undefined;

  const resolvedLabelTooltip = labelTooltip ?? defaultLabelTooltip;
  const resolvedContentTooltip = contentTooltip ?? defaultContentTooltip;

  const labelNode = (
    <Typography.Text
      style={{
        ...clampStyle,
        ...labelStyle,
        display: "inline-block",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
      className={cn("info-grid-label", clampClass, labelClassName)}
    >
      {label}:&nbsp;
    </Typography.Text>
  );

  const renderedLabel = wrapWithTooltip(labelNode, resolvedLabelTooltip);

  return (
    <div
      className={cn("info-grid-item", className)}
      // First column auto-fit to label width, second column takes remaining space
      style={{ gridTemplateColumns: `max-content minmax(0, 1fr)` }}
    >
      {renderedLabel}

      {isHtml
        ? wrapWithTooltip(
            <div
              style={{
                ...clampStyle,
                ...contentStyle,
                display: resolvedLines > 1 ? "-webkit-box" : "inline-block",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: resolvedLines > 1 ? undefined : "ellipsis",
              }}
              className={cn("info-grid-content", clampClass, contentClassName)}
              dangerouslySetInnerHTML={{ __html: sanitizedHtml ?? "" }}
            />,
            resolvedContentTooltip,
          )
        : wrapWithTooltip(
            <Typography.Text
              style={{
                ...clampStyle,
                ...contentStyle,
                display: resolvedLines > 1 ? "-webkit-box" : "inline-block",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: resolvedLines > 1 ? undefined : "ellipsis",
              }}
              className={cn("info-grid-content", clampClass, contentClassName)}
            >
              {content}
            </Typography.Text>,
            resolvedContentTooltip,
          )}
    </div>
  );
};

const TagItem = ({
  label,
  content,
  className,
  labelClassName,
  contentClassName,
  labelStyle,
  contentStyle,
  labelColumnWidth,
  lines = 1,
  labelTooltip,
  contentTooltip,
  tagProps,
}: // fullWidth được xử lý bởi Root component
InfoGridTagProps) => {
  const resolvedLines = Math.max(1, lines);
  const clampClass = getClampClass(resolvedLines);
  const clampStyle = getClampStyle(resolvedLines);

  const defaultLabelTooltip = typeof label === "string" || typeof label === "number" ? label : undefined;

  const defaultContentTooltip = typeof content === "string" || typeof content === "number" ? content : undefined;

  const resolvedLabelTooltip = labelTooltip ?? defaultLabelTooltip;
  const resolvedContentTooltip = contentTooltip ?? defaultContentTooltip;

  const labelNode = (
    <Typography.Text
      style={{ ...clampStyle, ...labelStyle }}
      className={cn("info-grid-label", clampClass, labelClassName)}
    >
      {label}
    </Typography.Text>
  );

  const baseTagStyle: CSSProperties = {
    color: "#fff",
    backgroundColor: "#1677ff",
    borderColor: "#1677ff",
    ...contentStyle,
  };

  const tagElement = (
    <Tooltip title={content}>
      <Tag
        {...tagProps}
        className={cn(
          "info-grid-tag line-clamp-1 max-w-full overflow-hidden text-ellipsis whitespace-nowrap px-3 py-1 font-semibold",
          clampClass,
          contentClassName,
          tagProps?.className,
        )}
        style={{
          ...baseTagStyle,
          ...tagProps?.style,
          display: "inline-block",
          maxWidth: "100%",
        }}
      >
        {content}
      </Tag>
    </Tooltip>
  );

  return (
    <div
      className={cn("info-grid-item", className)}
      // First column auto-fit to label width, second column takes remaining space
      style={{ gridTemplateColumns: `max-content minmax(0, 1fr)` }}
    >
      {wrapWithTooltip(labelNode, resolvedLabelTooltip)}
      {wrapWithTooltip(tagElement, resolvedContentTooltip)}
    </div>
  );
};

const InfoGrid = Root as typeof Root & { Item: typeof Item; Tag: typeof TagItem };

InfoGrid.Item = Item;
InfoGrid.Tag = TagItem;

export type { InfoGridItemProps, InfoGridProps, InfoGridTagProps };

export default InfoGrid;
